<template>
  <div class="w-full">
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-2xl font-bold text-[#00225E]">User Management</h2>
      <button
        @click="openCreateModal"
        class="px-4 py-2 bg-[#00225E] text-white rounded-md hover:bg-[#00225E]/90 focus:outline-none focus:ring-2 focus:ring-[#00225E] focus:ring-offset-2"
      >
        Add User
      </button>
    </div>

    <div class="mb-6">
      <div class="flex gap-4">
        <div class="flex-1">
          <input
            v-model="searchQuery"
            @input="searchUsers"
            type="text"
            placeholder="Search users by email, first name or last name..."
            class="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-[#00225E] focus:border-[#00225E]"
          />
        </div>
      </div>
    </div>

    <div class="bg-white shadow-sm rounded-lg overflow-hidden">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Companies
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created Date
              </th>
              <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-if="loading" class="animate-pulse">
              <td colspan="7" class="px-6 py-4 text-center">
                <div class="flex justify-center">
                  <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00225E]"></div>
                </div>
              </td>
            </tr>
            <tr v-else-if="users.length === 0">
              <td colspan="7" class="px-6 py-4 text-center text-gray-500">
                No users found
              </td>
            </tr>
            <tr v-else v-for="user in users" :key="user.id_user" class="hover:bg-gray-50">
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div class="flex-shrink-0 h-10 w-10">
                    <div class="h-10 w-10 rounded-full bg-[#00225E] flex items-center justify-center">
                      <span class="text-sm font-medium text-white">
                        {{ getUserInitials(user) }}
                      </span>
                    </div>
                  </div>
                  <div class="ml-4">
                    <div class="text-sm font-medium text-gray-900">
                      {{ user.first_name }} {{ user.last_name }}
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ user.email }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  :class="roleBadgeClass(user.role)"
                  class="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                >
                  {{ roleLabel(user.role) }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <span
                  v-if="user.company"
                  class="inline-flex px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                >
                  {{ user.company }}
                </span>
                <span v-else class="text-gray-400 text-xs">No company</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  :class="{
                    'bg-green-100 text-green-800': user.is_active,
                    'bg-blue-100 text-blue-800': !user.is_active
                  }"
                  class="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                >
                  {{ user.is_active ? 'Active' : 'Inactive' }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {{ formatDate(user.created_at) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div class="flex justify-end space-x-2">
                  <button
                    v-if="user.id_user !== currentUser?.id_user && canManageTargetUser(user)"
                    @click="openEditModal(user)"
                    class="text-indigo-600 hover:text-indigo-900 flex items-center"
                    title="Edit user"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    v-if="canToggleUser(user)"
                    @click="toggleUserStatus(user)"
                    :class="user.is_active ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'"
                    class="flex items-center"
                    :title="user.is_active ? 'Disable user' : 'Enable user'"
                  >
                    <svg v-if="user.is_active" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                    <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </button>
                  <button
                    v-if="canDeleteUser(user)"
                    @click="deleteUserConfirm(user)"
                    class="text-blue-600 hover:text-blue-900 flex items-center"
                    title="Delete user"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                  <button
                    v-if="user.id_user !== currentUser?.id_user && user.role !== 'superadmin' && user.must_change_password == 1"
                    @click="resendCredentials(user)"
                    :disabled="resendingCredentials === user.id_user"
                    class="text-green-600 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    title="Resend credentials email"
                  >
                    <svg v-if="resendingCredentials !== user.id_user" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <svg v-else class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </button>
                  <span v-else-if="user.id_user === currentUser?.id_user" class="text-gray-400 text-sm">
                    Cannot edit own account
                  </span>
                  <span v-else-if="user.role === 'superadmin'" class="text-gray-400 text-sm">
                    Cannot edit superadmin
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
      
    <!-- Pagination -->
    <div v-if="totalPages > 1" class="mt-6 flex items-center justify-between">
      <div class="text-sm text-gray-700">
        Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, total) }} of {{ total }} results
      </div>
      <div class="flex space-x-2">
        <button
          @click="changePage(currentPage - 1)"
          :disabled="currentPage === 1"
          class="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <span class="px-3 py-2 text-sm font-medium text-gray-700">
          Page {{ currentPage }} of {{ totalPages }}
        </span>
        <button
          @click="changePage(currentPage + 1)"
          :disabled="currentPage === totalPages"
          class="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>

    <!-- Create/Edit User Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div class="mt-3">
          <h3 class="text-lg font-medium text-gray-900 mb-4">
            {{ isEditing ? 'Edit User' : 'Add User' }}
          </h3>
          
          <form @submit.prevent="submitForm" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                v-model="form.email"
                type="email"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#00225E] focus:border-[#00225E]"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <input
                v-model="form.first_name"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#00225E] focus:border-[#00225E]"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input
                v-model="form.last_name"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#00225E] focus:border-[#00225E]"
              />
            </div>
            
            <div >
              <label class="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                v-model="form.role"
                :disabled="isEditing && userRole === 'admin' && form.role !== 'user'"
                :class="[
                  'w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#00225E] focus:border-[#00225E]',
                  isEditing && userRole === 'admin' && form.role !== 'user' ? 'bg-gray-100 cursor-not-allowed' : ''
                ]"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option v-if="canAssignOwner" value="owner">Owner</option>
              </select>
                <div v-if="isEditing && userRole === 'admin' && form.role !== 'user'" class="text-sm text-gray-500 mt-1">
                  Admin users can only change roles of normal users
                </div>
            </div>
            
            <div v-if="!isEditing">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Password will be auto-generated
              </label>
              <div class="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-500 text-sm">
                A secure password will be automatically generated for this user
              </div>
            </div>
            <div v-else>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div class="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-500 text-sm">
                Password cannot be changed
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Company</label>
              <div v-if="loadingCompanies" class="text-sm text-gray-500">
                Loading companies...
              </div>
              <select
                v-else
                v-model="selectedCompany"
                @change="updateCompany"
                :disabled="userRole === 'admin'"
                :class="[
                  'w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#00225E] focus:border-[#00225E]',
                  userRole === 'admin' ? 'bg-gray-100 cursor-not-allowed' : ''
                ]"
              >
                <option value="">Select a company</option>
                <option v-for="company in companies" :key="company" :value="company">
                  {{ company }}
                </option>
              </select>
              <div v-if="userRole === 'admin'" class="text-sm text-gray-500 mt-1">
                Admin users can only create users for their own company
              </div>
              <div v-else-if="companies.length === 0 && !loadingCompanies" class="text-sm text-gray-500 mt-1">
                No companies available
              </div>
            </div>
            
            
            <div class="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                @click="closeModal"
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                :disabled="submitting"
                class="px-4 py-2 text-sm font-medium text-white bg-[#00225E] rounded-md hover:bg-[#00225E]/90 disabled:opacity-50"
              >
                {{ submitting ? 'Saving...' : (isEditing ? 'Update' : 'Create') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'UserManagement',
  setup() {
    const store = useStore()
    
    // State
    const users = ref([])
    const loading = ref(false)
    const searchQuery = ref('')
    const currentPage = ref(1)
    const pageSize = ref(10)
    const total = ref(0)
    const showModal = ref(false)
    const isEditing = ref(false)
    const submitting = ref(false)
    const resendingCredentials = ref(null)
    
    // Form data
    const form = ref({
      email: '',
      first_name: '',
      last_name: '',
      password: '',
      role: 'user',
      company: ''
    })
    
    // Companies data
    const companies = ref([])
    const loadingCompanies = ref(false)
    const selectedCompany = ref('')
    
    // Computed
    const currentUser = computed(() => {
      const user = store.state.user || {}
      console.log('Current user from store:', user)
      return user
    })
    const userRole = computed(() => store.getters.userRole)
    const isSuperAdmin = computed(() => store.getters.isSuperAdmin)
    const isOwner = computed(() => store.getters.isOwner)
    const isAdmin = computed(() => store.getters.isAdmin || store.getters.isSuperAdmin)
    const canManageUsers = computed(() => store.getters.canManageUsers)
    // Only superadmin and owner can assign the Owner role
    const canAssignOwner = computed(() => isSuperAdmin.value || isOwner.value)
    const totalPages = computed(() => Math.ceil(total.value / pageSize.value))
    
    // Methods
    const getUserInitials = (user) => {
      const firstName = user.first_name || ''
      const lastName = user.last_name || ''
      return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase()
    }
    
    const formatDate = (dateString) => {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleDateString('en-US')
    }

    const roleLabel = (role) => {
      if (role === 'superadmin') return 'Super Admin'
      if (role === 'owner') return 'Owner'
      if (role === 'admin') return 'Admin'
      return 'User'
    }

    const roleBadgeClass = (role) => {
      if (role === 'superadmin') return 'bg-purple-100 text-purple-800'
      if (role === 'owner') return 'bg-indigo-100 text-indigo-800'
      if (role === 'admin') return 'bg-blue-100 text-blue-800'
      return 'bg-green-100 text-green-800'
    }

    // Mirrors the backend canManageTarget: superadmin is always protected;
    // owner can manage everything below superadmin; admin only same-company user/admin.
    const canManageTargetUser = (user) => {
      if (user.role === 'superadmin') return false
      if (isSuperAdmin.value || isOwner.value) return true
      if (userRole.value === 'admin') {
        if (user.company !== currentUser.value?.company) return false
        return user.role === 'user' || user.role === 'admin'
      }
      return false
    }

    const canToggleUser = (user) => {
      // Cannot toggle yourself
      if (user.id_user === currentUser.value?.id_user) return false
      // Respect role/company hierarchy (blocks superadmin and admin→owner)
      if (!canManageTargetUser(user)) return false
      // Admins can only toggle normal 'user' accounts (not other admins)
      if (userRole.value === 'admin' && user.role !== 'user') return false
      return true
    }

    const canDeleteUser = (user) => {
      // Cannot delete yourself
      if (user.id_user === currentUser.value?.id_user) return false
      // Respect role/company hierarchy (blocks superadmin and admin→owner)
      if (!canManageTargetUser(user)) return false
      // Admins can only delete normal 'user' accounts (not other admins)
      if (userRole.value === 'admin' && user.role !== 'user') return false
      return true
    }
    
    const loadUsers = async () => {
      loading.value = true
      try {
        const filters = {
          page: currentPage.value,
          limit: pageSize.value,
          search: searchQuery.value
        }
        
        console.log('Loading users with filters:', filters)
        console.log('Current user role:', userRole.value)
        
        const response = await store.dispatch('getUsers', filters)
        console.log('Users response:', response)
        
        if (response.ok) {
          users.value = response.data
          total.value = response.total
        } else {
          console.error('Error loading users:', response.error)
        }
      } catch (error) {
        console.error('Error loading users:', error)
      } finally {
        loading.value = false
      }
    }
    
    const loadCompanies = async () => {
      loadingCompanies.value = true
      try {
        console.log('Loading companies...')
        const response = await store.dispatch('getCompanies')
        console.log('Companies response:', response)
        if (response.ok) {
          companies.value = response.data
          console.log('Companies loaded:', companies.value)
        } else {
          console.error('Error loading companies:', response.error)
        }
      } catch (error) {
        console.error('Error loading companies:', error)
      } finally {
        loadingCompanies.value = false
      }
    }
    
    const searchUsers = () => {
      currentPage.value = 1
      loadUsers()
    }
    
    const changePage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page
        loadUsers()
      }
    }
    
    const updateCompany = () => {
      // Set single company selection
      form.value.company = selectedCompany.value
    }
    
    const openCreateModal = () => {
      isEditing.value = false
      form.value = {
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        role: 'user',
        company: userRole.value === 'admin' ? currentUser.value.company : ''
      }
      selectedCompany.value = userRole.value === 'admin' ? currentUser.value.company : ''
      showModal.value = true
    }
    
    const openEditModal = (user) => {
      isEditing.value = true
      form.value = {
        email: user.email,
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        password: '',
        role: user.role || 'user',
        company: user.company || ''
      }
      // Set selected company for single select
      selectedCompany.value = user.company || ''
      showModal.value = true
    }
    
    const closeModal = () => {
      showModal.value = false
      form.value = {
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        role: 'user',
        company: ''
      }
      selectedCompany.value = ''
    }
    
    const submitForm = async () => {
      submitting.value = true
      try {
        const userData = { ...form.value }
        
        // Remove password field - it will be auto-generated on backend
        delete userData.password
        
        // Prevent creating new administrators
        if (!isEditing.value) {
          userData.is_superadmin = false
        }
        
        // Prevent editing administrator role
        if (isEditing.value) {
          const originalUser = users.value.find(u => u.email === form.value.email)
          if (originalUser) {
            userData.is_superadmin = originalUser.is_superadmin
          }
        }
        
        let response
        if (isEditing.value) {
          const userId = users.value.find(u => u.email === form.value.email)?.id_user
          response = await store.dispatch('updateUser', { id: userId, userData })
        } else {
          response = await store.dispatch('createUser', userData)
        }
        
        if (response.ok) {
          closeModal()
          loadUsers()
          // Show generated password to admin
          if (!isEditing.value && response.generatedPassword) {
          }
        } else {
          alert('Error: ' + response.error)
        }
      } catch (error) {
        console.error('Error submitting form:', error)
        alert('Error processing request')
      } finally {
        submitting.value = false
      }
    }
    
    const resendCredentials = async (user) => {
      if (!confirm(`Are you sure you want to resend credentials to ${user.email}? A new password will be generated and sent via email.`)) {
        return
      }
      
      try {
        resendingCredentials.value = user.id_user
        
        const response = await store.dispatch('resendCredentials', user.id_user)
        
        if (response.ok) {
          alert(`Credentials email sent successfully to ${user.email}`)
        } else {
          alert('Error: ' + response.error)
        }
      } catch (error) {
        console.error('Error resending credentials:', error)
        alert('Error resending credentials')
      } finally {
        resendingCredentials.value = null
      }
    }
    
    const toggleUserStatus = async (user) => {
      const action = user.is_active ? 'disable' : 'enable'
      if (!confirm(`Are you sure you want to ${action} ${user.first_name} ${user.last_name}?`)) {
        return
      }
      
      try {
        const response = await store.dispatch('toggleUserActive', user.id_user)
        
        if (response.ok) {
          alert(`User ${action}d successfully`)
          loadUsers()
        } else {
          alert('Error: ' + response.error)
        }
      } catch (error) {
        console.error('Error toggling user status:', error)
        alert('Error toggling user status')
      }
    }
    
    const deleteUserConfirm = async (user) => {
      if (!confirm(`Are you sure you want to delete ${user.first_name} ${user.last_name}? This action will mark the user as deleted and cannot be undone.`)) {
        return
      }
      
      try {
        const response = await store.dispatch('deleteUser', user.id_user)
        
        if (response.ok) {
          alert('User deleted successfully')
          loadUsers()
        } else {
          alert('Error: ' + response.error)
        }
      } catch (error) {
        console.error('Error deleting user:', error)
        alert('Error deleting user')
      }
    }
    
    
    // Lifecycle
    onMounted(async () => {
      console.log('UserManagement mounted, checking auth...')
      console.log('Store state:', store.state)
      console.log('Store getters:', store.getters)
      
      // Check if user is authenticated
      if (!store.state.user) {
        console.log('No user found, checking auth...')
        await store.dispatch('checkAuth')
      }
      
      loadUsers()
      loadCompanies()
    })
    
    return {
      users,
      loading,
      searchQuery,
      currentPage,
      pageSize,
      total,
      showModal,
      isEditing,
      submitting,
      resendingCredentials,
      form,
      companies,
      loadingCompanies,
      selectedCompany,
      currentUser,
      userRole,
      canAssignOwner,
      totalPages,
      getUserInitials,
      formatDate,
      roleLabel,
      roleBadgeClass,
      canManageTargetUser,
      canToggleUser,
      canDeleteUser,
      loadUsers,
      toggleUserStatus,
      deleteUserConfirm,
      resendCredentials,
      loadCompanies,
      updateCompany,
      searchUsers,
      changePage,
      openCreateModal,
      openEditModal,
      closeModal,
      submitForm
    }
  }
}
</script>
